/**
 * useChunkedSpreadsheetUpload — driver del flusso BE-proxy → MinIO
 * S3 multipart per file > 32 MB (cap GFE su deployment GKE).
 *
 * Pattern SOTA giugno 2026 (vedi WebSearch + ``CHUNKED MULTIPART
 * UPLOAD`` block in BE ``enterprise_connectors.py``):
 *   1. ``start`` → ``{upload_id, object_key, part_size}``
 *   2. Per ogni chunk del ``File`` (sliced a ``part_size``):
 *       ``uploadSpreadsheetPart`` con 4 chunk paralleli (semaphore)
 *   3. ``complete`` con tutti gli etag → ``object_key`` finale
 *
 * Il ``object_key`` ritornato viene poi passato a
 * ``importSpreadsheet({ source_presigned_key })`` che esegue
 * effettivamente il parse + import nel DB.
 *
 * Progress: il listener riceve ``{loaded, total, percent}`` aggiornato
 * dopo OGNI chunk completato (16 MB di granularità). Per upload da
 * ~100 MB significa ~7 update — abbastanza fluido per una progress
 * bar che cresce a scatti visibili ma non rumorosi.
 *
 * Cancel: il caller può chiamare ``cancel()`` in qualunque momento.
 *   - Aborts gli upload_part in volo via AbortController
 *   - Calla ``abortSpreadsheetMultipart`` per liberare lo spazio MinIO
 *   - Il chiamante riceve un rejected Promise con ``new Error("canceled")``
 *
 * Resume: deliberatamente NON implementato in questa prima cut.
 * Lo state (upload_id + parts done) potrebbe essere persistito in
 * localStorage per supportare resume dopo refresh, ma non è banale
 * (il File handle si perde al reload). Per ora: errore terminale =
 * abort multipart + retry from scratch.
 */

import { useLingui } from "@lingui/react/macro";
import * as React from "react";

import {
  abortSpreadsheetMultipart,
  completeSpreadsheetMultipart,
  startMultipartSpreadsheetUpload,
  uploadSpreadsheetPart,
} from "../api/spreadsheetApi";

/** Quanti chunk in volo contemporaneamente. 4 è il sweet spot consigliato
 *  da TUS.io / Cloudflare Stream: ~3-4× throughput vs sequenziale,
 *  picco di memoria ragionevole (4 × part_size = ~64 MB). */
const MAX_PARALLEL_PARTS = 4;

export interface ChunkedUploadProgress {
  /** Byte completati (somma delle parti già caricate). */
  readonly loaded: number;
  /** Byte totali del file. */
  readonly total: number;
  /** Percentuale 0..100, arrotondata a 1 decimale per evitare flickering. */
  readonly percent: number;
}

export interface ChunkedUploadResult {
  /** Chiave MinIO da passare a ``importSpreadsheet({source_presigned_key})``. */
  readonly object_key: string;
  /** Byte totali confermati lato server (HEAD post-complete). */
  readonly size_bytes: number;
}

export type ChunkedUploadStatus =
  | "idle"
  | "starting"
  | "uploading"
  | "completing"
  | "success"
  | "error"
  | "canceled";

export interface UseChunkedSpreadsheetUploadResult {
  readonly status: ChunkedUploadStatus;
  readonly progress: ChunkedUploadProgress | null;
  readonly error: Error | null;
  /** Avvia l'upload del ``file``. Risolve con ``object_key`` finale. */
  readonly upload: (file: File) => Promise<ChunkedUploadResult>;
  /** Cancella l'upload in volo. No-op se ``status`` non è "uploading". */
  readonly cancel: () => void;
  /** Reset dello stato per un nuovo upload pulito. */
  readonly reset: () => void;
}

export function useChunkedSpreadsheetUpload(): UseChunkedSpreadsheetUploadResult {
  const { t } = useLingui();
  const [status, setStatus] = React.useState<ChunkedUploadStatus>("idle");
  const [progress, setProgress] = React.useState<ChunkedUploadProgress | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  // Cancel state — vive in ref per essere stabile fra render senza
  // forzare re-render quando cambia. Il ``cancel()`` accende il flag e
  // aborta tutti i fetch in volo.
  const cancelRef = React.useRef<{
    flag: boolean;
    controllers: Set<AbortController>;
    uploadId?: string;
    objectKey?: string;
  }>({ flag: false, controllers: new Set() });

  const reset = React.useCallback(() => {
    cancelRef.current = { flag: false, controllers: new Set() };
    setStatus("idle");
    setProgress(null);
    setError(null);
  }, []);

  const cancel = React.useCallback(() => {
    cancelRef.current.flag = true;
    // Abort tutti i PUT in volo — il caller dell'upload riceverà
    // l'errore e farà il cleanup multipart server-side.
    for (const ctrl of cancelRef.current.controllers) ctrl.abort();
    cancelRef.current.controllers.clear();
  }, []);

  const upload = React.useCallback(
    async (file: File): Promise<ChunkedUploadResult> => {
      reset();
      setStatus("starting");
      setProgress({ loaded: 0, total: file.size, percent: 0 });

      // 1. Start — chiede al BE upload_id + part_size canonico.
      let started;
      try {
        started = await startMultipartSpreadsheetUpload({
          filename: file.name,
          size_bytes: file.size,
          content_type: file.type || undefined,
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error("start failed");
        setStatus("error");
        setError(err);
        throw err;
      }
      cancelRef.current.uploadId = started.upload_id;
      cancelRef.current.objectKey = started.object_key;

      // 2. Calcola lo schema dei chunk e bilancia il parallelismo.
      const partSize = started.part_size;
      const totalParts = Math.ceil(file.size / partSize);
      if (totalParts > started.max_parts) {
        const err = new Error(
          t`File troppo grande per il chunking (${totalParts} > ${started.max_parts} parti).`,
        );
        setStatus("error");
        setError(err);
        throw err;
      }
      setStatus("uploading");

      // Counter atomico-condiviso aggiornato dopo ogni chunk completato.
      // ``loaded`` cresce monotonicamente — il render della progress bar
      // resta stabile anche con upload concorrenti che terminano
      // out-of-order.
      let loadedBytes = 0;
      const partResults: Array<{ part_number: number; etag: string }> = [];

      const uploadOne = async (partNumber: number): Promise<void> => {
        if (cancelRef.current.flag) throw new Error("canceled");
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const chunk = file.slice(start, end);

        const ctrl = new AbortController();
        cancelRef.current.controllers.add(ctrl);
        try {
          const res = await uploadSpreadsheetPart({
            upload_id: started.upload_id,
            object_key: started.object_key,
            part_number: partNumber,
            chunk,
            signal: ctrl.signal,
          });
          partResults.push({ part_number: res.part_number, etag: res.etag });
          loadedBytes += chunk.size;
          const percent = Math.round((loadedBytes / file.size) * 1000) / 10;
          setProgress({ loaded: loadedBytes, total: file.size, percent });
        } finally {
          cancelRef.current.controllers.delete(ctrl);
        }
      };

      // Worker-pool semplificato: ``MAX_PARALLEL_PARTS`` task in volo,
      // ognuno consuma la prossima part_number disponibile finché non
      // ci sono più chunks da inviare. Più solido di
      // ``Promise.all(chunks.map)`` che farebbe partire TUTTI i 30+
      // chunk simultaneamente (browser fa throttle a 6 socket per
      // origin e il BE soffre il picco di memoria).
      let nextPart = 1;
      const workers: Promise<void>[] = [];
      for (let w = 0; w < Math.min(MAX_PARALLEL_PARTS, totalParts); w++) {
        workers.push(
          (async () => {
            while (nextPart <= totalParts) {
              const myPart = nextPart++;
              await uploadOne(myPart);
            }
          })(),
        );
      }

      try {
        await Promise.all(workers);
      } catch (e) {
        const isCancel =
          cancelRef.current.flag ||
          (e instanceof DOMException && e.name === "AbortError") ||
          (e instanceof Error && e.message === "canceled");
        // Cleanup MinIO: che sia un cancel utente o un errore di rete,
        // chiamiamo abort così non lasciamo parti zombie nel bucket
        // (S3 le tiene fino a quando un Lifecycle policy non le pulisce).
        void abortSpreadsheetMultipart({
          upload_id: started.upload_id,
          object_key: started.object_key,
        }).catch(() => {
          // best-effort — il BE è già idempotente lato MinIO.
        });
        if (isCancel) {
          setStatus("canceled");
          const err = new Error(t`Upload annullato.`);
          setError(err);
          throw err;
        }
        const err = e instanceof Error ? e : new Error("upload failed");
        setStatus("error");
        setError(err);
        throw err;
      }

      // 3. Complete — invia tutti gli etag al BE che chiude la sessione
      // multipart e ci restituisce ``object_key`` (== ``started.object_key``)
      // + ``size_bytes`` confermati via HEAD.
      setStatus("completing");
      try {
        const completed = await completeSpreadsheetMultipart({
          upload_id: started.upload_id,
          object_key: started.object_key,
          parts: partResults.sort((a, b) => a.part_number - b.part_number),
        });
        setStatus("success");
        setProgress({ loaded: file.size, total: file.size, percent: 100 });
        return completed;
      } catch (e) {
        const err = e instanceof Error ? e : new Error("complete failed");
        setStatus("error");
        setError(err);
        throw err;
      }
    },
    [reset, t],
  );

  return { status, progress, error, upload, cancel, reset };
}
